import { Card, CardMedia } from '@mui/material'
import clsx from 'clsx'
import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ISystemAsset } from '@dl/declarations'
import { Button, CopyToClipboardButton } from '../../components/data/Button'
import { generateSearchParam, renderDate, renderSubtypeLabel } from '../../utils/helpers'
import { AssetsPath } from '../../utils/routes'
import { SystemTooltip } from '../ui/Tooltip'
import { CancelButton } from './shared'

import { BiPlus } from 'react-icons/bi'
import { MdOutlineArticle, MdOutlineEdit } from 'react-icons/md'

// const MAX_KEYWORDS_IN_VIEW = 3

interface CommonProps {
    asset: ISystemAsset;
    actions: {
        useImage: {
            show: boolean;
            action?: (asset: ISystemAsset) => void;
            component?: React.ReactNode;
        };
        editImage: {
            show: boolean
            component?: React.ReactNode;
        }
    };
    subtypeOptions?: {
        showChips: boolean
    }
}

export const ImageAsset: React.FC<CommonProps> = (props) => {
    const {
        asset,
        actions,
        subtypeOptions = {
            showChips: true
        }
    } = props;

    const [searchParams, setSearchParams] = useSearchParams()

    const selectedId = searchParams.get(generateSearchParam(AssetsPath, 'selected_id'))

    const handleUseImage = useCallback((e: React.MouseEvent<HTMLButtonElement>, asset: ISystemAsset) => {
        e.stopPropagation();
        actions.useImage.action && actions.useImage.action(asset)
        setSearchParams((prevParams) => {
            prevParams.delete(generateSearchParam(AssetsPath, 'selected_id'))
            return prevParams
        })
    }, [actions.useImage, setSearchParams])

    const handleClickImage = useCallback((e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>, asset: ISystemAsset | null) => {
        e.stopPropagation();

        setSearchParams((prevParams) => {
            if (asset) {
                prevParams.set(generateSearchParam(AssetsPath, 'selected_id'), asset._id)
            }
            else {
                prevParams.delete(generateSearchParam(AssetsPath, 'selected_id'))
            }

            return prevParams
        }, {
            replace: true,
        })
    }, [setSearchParams])

    const handleUpdateImage = useCallback((e: React.MouseEvent<HTMLButtonElement>, asset: ISystemAsset) => {
        e.stopPropagation();

        setSearchParams((prevParams) => {
            prevParams.set(generateSearchParam(AssetsPath, 'mode'), 'update')
            prevParams.set(generateSearchParam(AssetsPath, "id"), asset._id)
            return prevParams
        })
    }, [setSearchParams])

    const isSelected = selectedId === asset._id;

    return (
        <div
            key={asset._id}
            className={clsx(
                'cursor-pointer relative border overflow-hidden rounded-lg',
                isSelected ? 'border-cs-600' : 'border-cs-700',
            )}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => handleClickImage(e, asset)}
        >
            {subtypeOptions.showChips && asset.subtypesObjects!.length > 0 && (
                <div
                    className={clsx(
                        'absolute top-2 left-2 z-10 flex flex-wrap gap-1',
                    )}
                >
                    {asset.subtypesObjects!.map((subtype) => (
                        <div
                            key={subtype._id}
                            className='bg-white text-cs-700 font-semibold p-1'
                        >
                            {renderSubtypeLabel(subtype, "SHORT")}
                        </div>
                    ))}
                </div>
            )}

            <div className='cs-file-input-placeholder-bg relative overflow-hidden rounded-t-lg group w-full aspect-[1.85/1]'>
                <img
                    src={asset.thumbnail_url || asset.src_url}
                    alt={asset.headline || 'Thumbnail'}
                    className='absolute inset-0 size-full'
                    loading='lazy'
                    decoding='async'
                    fetchPriority='low'
                    style={{
                        imageRendering: '-webkit-optimize-contrast'
                    }}
                />

                <div
                    className={clsx(
                        'absolute inset-0 z-20 pointer-events-none',
                        'bg-cs-normal-blue transition-opacity duration-150 motion-reduce:transition-none',
                        isSelected ? 'opacity-40' : 'opacity-0 group-hover:opacity-40',
                    )}
                />
            </div>

            {(isSelected && Object.keys(actions).length > 0) && (
                <div
                    className={clsx(
                        'absolute inset-0 z-30 mx-auto max-w-3/5 flex flex-col gap-2 items-center justify-center',
                    )}
                >
                    {actions.useImage.show && (
                        <>
                            {actions.useImage.component ? (
                                actions.useImage.component
                            ) : (
                                <Button
                                    as='button'
                                    type='button'
                                    inputVariant='white_text_blue_bg'
                                    rightIcon={<BiPlus size={24} />}
                                    showHover={false}
                                    showBorders={false}
                                    inputSize=''
                                    className='outline outline-black w-fit max-w-full lg:w-42 xl:w-52'
                                    onClick={(e) => handleUseImage(e, asset)}
                                >
                                    Add {asset.mime_type}
                                </Button>
                            )}
                        </>
                    )}

                    {actions.editImage.show && (
                        <Button
                            as='button'
                            type='button'
                            inputVariant='white_text_deep_ocean_bg'
                            rightIcon={<MdOutlineEdit size={24} />}
                            showHover={false}
                            showBorders={false}
                            inputSize=''
                            className='outline outline-black w-fit max-w-full lg:w-42 xl:w-52'
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleUpdateImage(e, asset)}
                        >
                            Update {asset.mime_type}
                        </Button>
                    )}

                    <Button
                        type='button'
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleClickImage(e, null)}
                        rightIcon={<CancelButton size={24} />}
                        showHover={false}
                        showBorders={false}
                        inputSize=''
                        className='outline outline-black w-fit max-w-full lg:w-42 xl:w-52'
                    >
                        Cancel selection
                    </Button>
                </div>
            )}

            <div
                className='p-1 rounded-b-lg bg-white flex flex-col gap-2'
            >
                <p
                    className="text-[16px] font-semibold text-cs-700 truncate ..."
                >
                    {asset.headline || <>&nbsp;</>}
                </p>

                <div className='flex items-center justify-between'>
                    <SystemTooltip
                        message={`
                            ${(asset.main_asset_story_aggregate +
                                asset.homepage_asset_story_aggregate +
                                asset.social_media_asset_story_aggregate)} published articles

                            ${asset.last_used_at ? `<br/> Last used on ${renderDate(asset.last_used_at)}` : ''}
                        `}
                    >
                        <div
                            className='flex items-center gap-2 px-2 bg-cs-200'
                        >
                            <MdOutlineArticle size={14} />
                            <span>
                                {
                                    asset.main_asset_story_aggregate +
                                    asset.homepage_asset_story_aggregate +
                                    asset.social_media_asset_story_aggregate
                                }
                            </span>
                        </div>
                    </SystemTooltip>

                    <p
                        className='text-[16px] font-semibold m-0 text-cs-700'
                    >
                        <span className='font-bold'>Created:</span> <span><time dateTime={asset.created_at}>{renderDate(asset.created_at)}</time></span>
                    </p>

                    {asset.src_url && (
                        <CopyToClipboardButton
                            text={asset.src_url}
                        />
                    )}
                </div>
            </div>
        </div>
    )

    return (
        <Card
            sx={{
                cursor: 'pointer',
                border: isSelected ? '2px solid #2563EB' : '1px solid #374151',
                overflow: 'hidden',
                borderRadius: '0.5rem',
                '&:hover': {
                    borderColor: '#2563EB',
                },
                maxHeight: '250px',
            }}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => handleClickImage(e, asset)}
        >
            {/* <CardHeader
                avatar={
                    <Avatar sx={{ bgcolor: red[500] }} aria-label="recipe">
                        R
                    </Avatar>
                }
                action={
                    <IconButton aria-label="settings">
                        <MoreVertIcon />
                    </IconButton>
                }
                title="Shrimp and Chorizo Paella"
                subheader="September 14, 2016"
            /> */}

            <CardMedia
                component="img"
                height="200"
                image={asset.thumbnail_url || asset.src_url}
                alt={asset.headline || 'Thumbnail'}
            />

            {/* <CardContent>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    This impressive paella is a perfect party dish and a fun meal to cook
                    together with your guests. Add 1 cup of frozen peas along with the mussels,
                    if you like.
                </Typography>
            </CardContent> */}

            {/* <CardActions disableSpacing>
                <IconButton aria-label="add to favorites">
                    <FavoriteIcon />
                </IconButton>
                <IconButton aria-label="share">
                    <ShareIcon />
                </IconButton>
                <ExpandMore
                    expand={expanded}
                    onClick={handleExpandClick}
                    aria-expanded={expanded}
                    aria-label="show more"
                >
                    <ExpandMoreIcon />
                </ExpandMore>
            </CardActions> */}

            {/* <Collapse in={expanded} timeout="auto" unmountOnExit>
                <CardContent>
                    <Typography sx={{ marginBottom: 2 }}>Method:</Typography>
                    <Typography sx={{ marginBottom: 2 }}>
                        Heat 1/2 cup of the broth in a pot until simmering, add saffron and set
                        aside for 10 minutes.
                    </Typography>
                    <Typography sx={{ marginBottom: 2 }}>
                        Heat oil in a (14- to 16-inch) paella pan or a large, deep skillet over
                        medium-high heat. Add chicken, shrimp and chorizo, and cook, stirring
                        occasionally until lightly browned, 6 to 8 minutes. Transfer shrimp to a
                        large plate and set aside, leaving chicken and chorizo in the pan. Add
                        pimentón, bay leaves, garlic, tomatoes, onion, salt and pepper, and cook,
                        stirring often until thickened and fragrant, about 10 minutes. Add
                        saffron broth and remaining 4 1/2 cups chicken broth; bring to a boil.
                    </Typography>
                    <Typography sx={{ marginBottom: 2 }}>
                        Add rice and stir very gently to distribute. Top with artichokes and
                        peppers, and cook without stirring, until most of the liquid is absorbed,
                        15 to 18 minutes. Reduce heat to medium-low, add reserved shrimp and
                        mussels, tucking them down into the rice, and cook again without
                        stirring, until mussels have opened and rice is just tender, 5 to 7
                        minutes more. (Discard any mussels that don&apos;t open.)
                    </Typography>
                    <Typography>
                        Set aside off of the heat to let rest for 10 minutes, and then serve.
                    </Typography>
                </CardContent>
            </Collapse> */}
        </Card>
    );
}